import { useCallback, useEffect, useState, useRef } from "react";
import { ClientsDropdown, ProjectsDropdown, ProcessTypesDropdown } from "./DropdownV2";
import { calibrationService } from "../../../services/CalibrationService";
import { useLocation, useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { makeStyles } from "@mui/styles";
import _ from "lodash";

import RunningProcessesPanel from "./RunningProcessesPanel";
import CompletedProcessesPanel from "./CompletedProcessesPanel";
import { parseISOToEpoch } from "../../../utils/parseISOToEpoch";

const TEN_SECONDS_IN_MS = 10000;

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        width: "60%",
        margin: "0 auto",
        padding: theme.spacing(3),
    },
    selectContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        width: "125%",
        marginBottom: theme.spacing(2),
    },
    dropdownWrapper: {
        flex: "1 1 200px",
        minWidth: 200,
    },
    dropdownWithInfo: {
        display: "flex",
        alignItems: "center",
        width: "100%",
    },
    error: {
        marginTop: theme.spacing(1),
        fontWeight: "bold",
        fontSize: "20px",
        color: "red",
    },
    panelContainer: {
        display: "flex",
        flexDirection: "column",
        width: "125%",
        height: "100%",
    },
    processTypeDescription: {
        position: "absolute",
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        border: "1px solid #ccc",
        borderRadius: "8px",
        padding: theme.spacing(1),
        maxWidth: "200px",
        fontSize: "14px",
        boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.1)",
        zIndex: 2,
    },
    modalContent: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: theme.spacing(2),
        width: "500px",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        boxShadow: theme.shadows[5],
        outline: "none",
        textAlign: "center",
    },
    iconButtonContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    tooltip: {
        backgroundColor: "#f5f5f9",
        color: "rgba(0, 0, 0, 0.87)",
        maxWidth: 220,
        fontSize: theme.typography.pxToRem(12),
        border: "1px solid #dadde9",
        borderRadius: "4px",
        boxShadow: theme.shadows[1],
    },
    dropdownStyle: {
        minWidth: 120,
        maxWidth: 300,
        margin: theme.spacing(1),
    },
}));

// In order to use this custom hook, we have to have the naming convention of "use<function name>"
const useRefreshJobs = (fetchProcesses) => {
    useEffect(() => {
        const fetchJobsInterval = setInterval(() => {
            fetchProcesses();
        }, TEN_SECONDS_IN_MS);

        return () => clearInterval(fetchJobsInterval);
    }, [fetchProcesses]);
};

export default function ProgressViewer() {
    const classes = useStyles();
    const [clientValue, setClientValue] = useState("");
    const [projectValue, setProjectValue] = useState("");
    const [processTypeValues, setProcessTypeValues] = useState([]);
    const [processTypes, setProcessTypes] = useState([]);
    const [processesLoading, setProcessesLoading] = useState(false);
    const [currentlyRunningProcesses, setCurrentlyRunningProcesses] = useState([]);
    const [completedProcesses, setCompletedProcesses] = useState([]);
    const [error, setError] = useState(null);

    const [timeBeforeStartInDays, setTimeBeforeStartInDays] = useState(1);
    const [selectedProcesses, setSelectedProcesses] = useState([]);

    // Embed the state into the URL
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const clientURL = queryParams.get("client");
    const projectURL = queryParams.get("project");
    const processTypesURL = queryParams.get("processTypes");

    const errorTimeoutRef = useRef(null);
    const initializedFromURL = useRef(false);

    // For setting error messages to show for only 10s
    const setErrorWithTimeout = useCallback((errorInput, timeoutInMs) => {
        // Ensure we only set string error messages (not error objects)
        const errorMessage = typeof errorInput === 'string' ? errorInput : (errorInput?.message || String(errorInput));
        setError(errorMessage);

        // Clear any existing timeout
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
        }

        // Clear error state after timeout
        errorTimeoutRef.current = setTimeout(() => setError(null), timeoutInMs);
    }, []);

    const fetchProcesses = useCallback(async () => {
        try {
            setProcessesLoading(true);
            const runningResult = await calibrationService.getRunningProcessesByClientAndProject(
                clientValue || "_",
                projectValue || "_",
                processTypeValues
            );
            const uniqueRunning = [
                ...new Map(runningResult.data.map((p) => [p.process_run_id, p])).values(),
            ];
            setCurrentlyRunningProcesses(uniqueRunning);

            if (clientValue || projectValue || processTypeValues.length > 0) {
                const completedResult = await calibrationService.getCompletedProcesses(
                    clientValue || "_",
                    projectValue || "_",
                    processTypeValues,
                    null,
                    timeBeforeStartInDays
                );
                setCompletedProcesses(
                    _(completedResult.data)
                        .sortBy([(obj) => parseISOToEpoch(obj.completed_at)])
                        .reverse()
                        .value()
                );
            } else {
                setCompletedProcesses([]);
            }
        } catch (error) {
            const errorMessage = error?.message || String(error);
            console.error(`There was an error fetching processes, ${errorMessage}`);
            setErrorWithTimeout(`Failed to fetch processes: ${errorMessage}`, 10000);
        } finally {
            setProcessesLoading(false);
        }
    }, [clientValue, projectValue, processTypeValues, timeBeforeStartInDays, setErrorWithTimeout]);

    const handleSetProcessesFailed = async () => {
        if (selectedProcesses.length === 0) {
            setErrorWithTimeout("No processes selected", 10000);
            return;
        }

        try {
            setProcessesLoading(true);

            // Execute POST request
            await calibrationService.setSelectedProcessesFailed(selectedProcesses);

            // Immediately clear all selections - no state maintenance
            setSelectedProcesses([]);

            /*
             Do not maintain state of running processes with React.
             The backend logic sets all matching run_ids to failed whether
             or not they are unique. React expects the keys to be unique.
             so let the db be the source of truth.
             */
            setCurrentlyRunningProcesses([]);

            // Fetch fresh data from database
            await fetchProcesses();
        } catch (error) {
            const errorMessage = error?.message || String(error);
            console.error(`There was an error setting processes as failed, ${errorMessage}`);
            setErrorWithTimeout(`Failed to set processes as failed: ${errorMessage}`, 10000);
        } finally {
            setProcessesLoading(false);
        }
    };

    // Mount the refresh jobs interval so that we can poll for job progress every 10 seconds
    useRefreshJobs(fetchProcesses);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, []);

    // Initialize from URL on mount
    useEffect(() => {
        if (!initializedFromURL.current && clientURL) {
            initializedFromURL.current = true;
            setClientValue(clientURL);
            if (projectURL) {
                setTimeout(() => setProjectValue(projectURL), 100);
            }
            if (processTypesURL) {
                setTimeout(() => setProcessTypeValues(processTypesURL.split(",").filter(Boolean)), 200);
            }
        }
    }, [clientURL, projectURL, processTypesURL]);

    // Clear downstream values when client changes
    useEffect(() => {
        if (initializedFromURL.current) {
            setProjectValue("");
            setProcessTypeValues([]);
            setCurrentlyRunningProcesses([]);
            setCompletedProcesses([]);
        }
    }, [clientValue]);

    // Clear process types when project changes
    useEffect(() => {
        if (initializedFromURL.current && projectValue === "") {
            setProcessTypeValues([]);
            setCurrentlyRunningProcesses([]);
            setCompletedProcesses([]);
        }
    }, [projectValue]);

    // Fetch jobs when process types or time range changes
    useEffect(() => {
        setError(null);
        fetchProcesses();
    }, [processTypeValues, timeBeforeStartInDays]);

    // Update the URL with the current state of the dropdowns
    useEffect(() => {
        const queryParams = new URLSearchParams();
        if (clientValue) {
            queryParams.append("client", clientValue);
        }
        if (projectValue) {
            queryParams.append("project", projectValue);
        }
        if (processTypeValues.length > 0) {
            queryParams.append("processTypes", processTypeValues.join(","));
        }

        navigate({
            search: queryParams.toString(),
        });
    }, [clientValue, projectValue, processTypeValues, navigate]);

    const handleProcessTypesLoaded = useCallback((options) => {
        setProcessTypes(options);
    }, []);

    return (
        <div className={classes.formContainer}>
            <h1>Progress Viewer</h1>
            <div className={classes.selectContainer}>
                <div className={classes.dropdownWrapper}>
                    <ClientsDropdown
                        value={clientValue}
                        setValue={setClientValue}
                        setError={setErrorWithTimeout}
                    />
                </div>
                <div className={classes.dropdownWrapper}>
                    <ProjectsDropdown
                        clientName={clientValue}
                        value={projectValue}
                        setValue={setProjectValue}
                        setError={setErrorWithTimeout}
                    />
                </div>
                <div className={classes.dropdownWrapper}>
                    <div className={classes.dropdownWithInfo}>
                        <ProcessTypesDropdown
                            clientName={clientValue}
                            projectName={projectValue}
                            value={processTypeValues}
                            setValue={setProcessTypeValues}
                            setError={setErrorWithTimeout}
                            onOptionsLoaded={handleProcessTypesLoaded}
                        />
                        {processTypeValues.length === 1 && (
                            <Tooltip
                                title={
                                    <>
                                        <Typography color="inherit">Process Description:</Typography>
                                        {processTypes.find((obj) => obj.value === processTypeValues[0])?.description}
                                    </>
                                }
                                classes={{
                                    tooltip: classes.tooltip,
                                }}
                            >
                                <IconButton size="small" sx={{ marginLeft: 1 }}>
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>

            {error && <Typography className={classes.error}>{error}</Typography>}

            {selectedProcesses.length > 0 && (
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleSetProcessesFailed}
                    disabled={processesLoading}
                    sx={{ marginBottom: 2 }}
                >
                    Set Selected Processes as Failed ({selectedProcesses.length})
                </Button>
            )}

            <div className={classes.panelContainer}>
                <RunningProcessesPanel
                    processes={currentlyRunningProcesses}
                    refreshJobs={fetchProcesses}
                    processType={processTypeValues.join(",")}
                    isLoading={processesLoading}
                    enableMultiSelect={true}
                    selectedProcesses={selectedProcesses}
                    setSelectedProcesses={setSelectedProcesses}
                />
                <CompletedProcessesPanel
                    processes={completedProcesses}
                    setTimeBeforeStartInDays={setTimeBeforeStartInDays}
                />
            </div>
        </div>
    );
}
